export interface ImageCompressionOptions {
  maxWidth?: number;
  maxHeight?: number;
  quality?: number;
  mimeType?: string;
}

const DEFAULT_OPTIONS: Required<ImageCompressionOptions> = {
  maxWidth: 1280,
  maxHeight: 1280,
  quality: 0.75,
  mimeType: 'image/jpeg',
};

/**
 * Calculates proportionally scaled dimensions maintaining aspect ratio.
 */
export const calculateDimensions = (
  width: number,
  height: number,
  maxWidth: number,
  maxHeight: number
): { width: number; height: number } => {
  if (width <= 0 || height <= 0) {
    return { width: Math.max(1, width), height: Math.max(1, height) };
  }

  if (width <= maxWidth && height <= maxHeight) {
    return { width, height };
  }

  const ratio = Math.min(maxWidth / width, maxHeight / height);
  return {
    width: Math.max(1, Math.round(width * ratio)),
    height: Math.max(1, Math.round(height * ratio)),
  };
};

/**
 * Converts a File or Blob into a base64 Data URL.
 */
export const fileToDataUrl = (file: File | Blob): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = (error) => reject(error);
    reader.readAsDataURL(file);
  });
};

/**
 * Converts a base64 Data URL to a File object.
 */
export const dataUrlToFile = (dataUrl: string, filename: string): File => {
  const arr = dataUrl.split(',');
  const mimeMatch = arr[0].match(/:(.*?);/);
  const mime = mimeMatch ? mimeMatch[1] : 'image/jpeg';
  const bstr = atob(arr[1] || arr[0]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }
  return new File([u8arr], filename, { type: mime });
};

/**
 * Compresses an image (File, Blob, or base64 Data URL) by resizing it
 * and lowering its quality to reduce file size drastically (typically >90% reduction).
 * 
 * Returns a Promise that resolves with the compressed base64 Data URL.
 */
export const compressImage = async (
  input: File | Blob | string,
  options?: ImageCompressionOptions
): Promise<string> => {
  const mergedOptions = { ...DEFAULT_OPTIONS, ...options };
  const { maxWidth, maxHeight, quality, mimeType } = mergedOptions;

  // If input is empty string, return as is
  if (typeof input === 'string' && !input.trim()) {
    return input;
  }

  // Handle environment without DOM (e.g. some node environments)
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    if (typeof input === 'string') return input;
    return fileToDataUrl(input);
  }

  let srcUrl = '';
  let isObjectUrl = false;

  if (typeof input === 'string') {
    // If it is already a data URL or base64
    srcUrl = input.startsWith('data:') ? input : `data:${mimeType};base64,${input}`;
  } else {
    // File or Blob
    if (typeof URL !== 'undefined' && typeof URL.createObjectURL === 'function') {
      srcUrl = URL.createObjectURL(input);
      isObjectUrl = true;
    } else {
      srcUrl = await fileToDataUrl(input);
    }
  }

  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const image = new Image();
      image.crossOrigin = 'anonymous';

      const timer = setTimeout(() => {
        reject(new Error('Timeout al cargar la imagen para compresión'));
      }, 1500);

      image.onload = () => {
        clearTimeout(timer);
        resolve(image);
      };
      image.onerror = (err) => {
        clearTimeout(timer);
        reject(new Error(`Error al cargar la imagen para compresión: ${err}`));
      };
      image.src = srcUrl;
    });

    const naturalWidth = img.naturalWidth || img.width;
    const naturalHeight = img.naturalHeight || img.height;

    const { width, height } = calculateDimensions(naturalWidth, naturalHeight, maxWidth, maxHeight);

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;

    const ctx = canvas.getContext('2d');
    if (!ctx) {
      // Fallback if canvas context is unavailable
      return typeof input === 'string' ? srcUrl : await fileToDataUrl(input);
    }

    // Enable smooth image scaling
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';

    // Fill white background for transparent images converted to JPEG
    if (mimeType === 'image/jpeg' || mimeType === 'image/jpg') {
      ctx.fillStyle = '#FFFFFF';
      ctx.fillRect(0, 0, width, height);
    }

    ctx.drawImage(img, 0, 0, width, height);

    const compressedDataUrl = canvas.toDataURL(mimeType, quality);

    // Verify if canvas exported valid data URL
    if (compressedDataUrl && compressedDataUrl.startsWith('data:')) {
      return compressedDataUrl;
    }

    return typeof input === 'string' ? srcUrl : await fileToDataUrl(input);
  } catch (error) {
    console.warn('Compresión de imagen falló, utilizando formato original:', error);
    if (typeof input === 'string') return srcUrl;
    return await fileToDataUrl(input);
  } finally {
    if (isObjectUrl && srcUrl) {
      try {
        URL.revokeObjectURL(srcUrl);
      } catch {
        // ignore cleanup error
      }
    }
  }
};

/**
 * Compresses an image File and returns a compressed File object.
 */
export const compressImageToFile = async (
  file: File,
  options?: ImageCompressionOptions
): Promise<File> => {
  const compressedDataUrl = await compressImage(file, options);
  const originalName = file.name || 'image.jpg';
  const nameWithoutExt = originalName.substring(0, originalName.lastIndexOf('.')) || originalName;
  const newName = `${nameWithoutExt}.jpg`;
  return dataUrlToFile(compressedDataUrl, newName);
};
